import { type SwiperContainer, register } from 'swiper/element/bundle'
import type { SwiperProps, SwiperSlideProps } from 'swiper/react'
import { getTemplateProps } from './swiper-templates'
register()

const SELECTOR = 'swiper-container[init="false"][data-yt-template]'

declare module 'swiper/element/bundle' {
  interface SwiperContainer {
    // Being a HTML Custom Element, these methods exist even if they are not declared
    disconnectedCallback(): void
    connectedCallback(): void

    // Other non-public methods and properties
    cssStyles(): string
    slideSlots?: number
  }
}

function wrapWithClippingElement(element: HTMLElement) {
  if (
    element &&
    !element?.parentElement?.classList?.contains('swiper-wrapper-clip')
  ) {
    const overflowWrapper = document.createElement('div')
    overflowWrapper.classList.add('swiper-wrapper-clip')
    if (element.parentElement && element.previousElementSibling) {
      element.parentElement.insertBefore(overflowWrapper, element)
      element.previousElementSibling.appendChild(element)
    }
  }
}

// Copied from node_modules/swiper/swiper-element-bundle.mjs
const setStyle = (shadowRoot: ShadowRoot | null, styles: string) => {
  if (!shadowRoot) {
    return
  }
  if (typeof CSSStyleSheet !== 'undefined' && shadowRoot.adoptedStyleSheets) {
    const styleSheet = new CSSStyleSheet()
    styleSheet.replaceSync(styles)
    shadowRoot.adoptedStyleSheets = [styleSheet]
  } else {
    const styleTags = Array.from(shadowRoot.querySelectorAll('style'))
    for (const styleTag of styleTags) {
      shadowRoot.removeChild(styleTag)
    }
    const style = document.createElement('style')
    // @ts-ignore
    style.rel = 'stylesheet'
    style.textContent = styles
    shadowRoot.appendChild(style)
  }
}

function querySelectorAllWithParent<T extends Element>(
  el: Node | NodeList | null | undefined,
  selector: string
) {
  const result: T[] = []
  const root = el instanceof NodeList ? [...el] : el ? [el] : []
  for (const node of root) {
    if (!(node instanceof Element)) {
      continue
    }
    if (node.matches(selector)) {
      result.push(node as T)
    }
    result.push(...node.querySelectorAll<T>(selector))
  }
  return result
}

function updateTemplate(swiper: SwiperContainer) {
  swiper.disconnectedCallback()
  const nextProps = getTemplateProps(swiper.dataset.ytTemplate)
  Object.assign(swiper, nextProps)
  swiper.dataset.ytPreferredNavigation = swiper.dataset.ytNavigation
  swiper.connectedCallback()
  swiper.initialize()

  // Copied from node_modules/swiper/swiper-element-bundle.mjs
  let localStyles = swiper.cssStyles()
  if (swiper.slideSlots) {
    localStyles = localStyles.replace(/::slotted\(([a-z-0-9.]*)\)/g, '$1')
  }
  setStyle(swiper.shadowRoot, localStyles)
}

const resizeObserver = new ResizeObserver((entries) => {
  for (const entry of entries) {
    const wrapper = entry.target
    const swiper = entry.target.querySelector<SwiperContainer>(SELECTOR)
    const arrow = swiper?.shadowRoot?.querySelector('[part="button-prev"]')
    if (!swiper || !(arrow instanceof Element)) {
      continue
    }
    const outerWidth = wrapper.clientWidth
    const sliderWidth = swiper.clientWidth
    const navigationSidesOffset = Number.parseFloat(
      window.getComputedStyle(arrow).left
    )
    const arrowWidth = arrow.clientWidth + 2 * navigationSidesOffset

    swiper.dataset.ytNavigationOutside = String(
      outerWidth >= sliderWidth + 2 * arrowWidth
    )

    const slides = Array.from(swiper.children)

    const allSlidesVisible =
      slides.length < 2 ||
      slides.every((slide) => slide.classList.contains('swiper-slide-visible'))

    swiper.dataset.ytNavigation = allSlidesVisible
      ? 'false'
      : swiper.dataset.ytPreferredNavigation

    // const sliderHeight = swiper.clientHeight
    // swiper.classList.toggle('large-navigation', sliderHeight > 500)
  }
})

function onMutation(mutations: Partial<MutationRecord>[]) {
  for (const mutation of mutations) {
    const addedSliders = querySelectorAllWithParent<SwiperContainer>(
      mutation.addedNodes,
      SELECTOR
    )
    for (const slider of addedSliders) {
      updateTemplate(slider)
      if (slider.parentElement) {
        resizeObserver.observe(slider.parentElement)
      }

      // wrap the ".slider-wrapper" inside a new container element that hides overflow
      // when "data-yt-hide-overflow" is set on the container
      const wrapper =
        slider.shadowRoot?.querySelector<HTMLElement>('.swiper-wrapper')
      if (wrapper && slider.dataset.ytHideOverflow !== undefined) {
        wrapWithClippingElement(wrapper)
      }
    }

    const updatedSliders = querySelectorAllWithParent<SwiperContainer>(
      mutation.type === 'attributes' ? mutation.target : null,
      SELECTOR
    )
    updatedSliders.forEach(updateTemplate)

    const removedSliders = querySelectorAllWithParent<SwiperContainer>(
      mutation.removedNodes,
      SELECTOR
    )
    for (const slider of removedSliders) {
      if (slider.parentElement) {
        resizeObserver.unobserve(slider.parentElement)
      }
      resizeObserver.unobserve(slider)
    }
  }
}

onMutation([
  {
    addedNodes: document.querySelectorAll('body')
  }
])

const mutationObserver = new MutationObserver(onMutation)

mutationObserver.observe(document.body, {
  childList: true,
  subtree: true,
  attributeFilter: ['data-yt-template']
})

declare global {
  namespace JSX {
    interface IntrinsicElements {
      'swiper-container': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> &
          Omit<SwiperProps, 'init'> & {
            init?: 'false'
            'data-yt-template'?: string
          },
        HTMLElement
      >
      'swiper-slide': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & SwiperSlideProps,
        HTMLElement
      >
    }
  }
}
